import React, { useContext, useEffect, useState } from 'react';
import QuizrrStampLight from '../../../../../apps/frontend/public/icons/quizrrStampLight.svg';
import QuizrrStampDark from '../../../../../apps/frontend/public/icons/quizrrStampDark.svg';
import { ThemeContext } from '../../../../../apps/frontend/src/context/ThemeStore';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { Drawer, DrawerContent } from '../../components/ui/drawer';
import { Button } from '../../components/ui/button';
import { X, ChevronRight } from 'lucide-react';

/**
 * Format scores: whole numbers stay integer; fractional values cap at 2 decimal places (trims float noise).
 */
function formatScoreDisplay(value) {
  if (value === null || value === undefined || value === '') return '0';
  const n = Number(value);
  if (Number.isNaN(n)) return '0';
  const roundedInt = Math.round(n);
  if (Math.abs(n - roundedInt) < 1e-6) return String(roundedInt);
  return parseFloat(n.toFixed(2)).toString();
}

function getEffectiveMarkScored(item) {
  const positive = Number(item?.positiveScore);
  const negative = Number(item?.negativeScore);
  const hasPosOrNeg = (!Number.isNaN(positive) && positive !== 0) || (!Number.isNaN(negative) && negative !== 0);
  if (hasPosOrNeg) {
    return (Number.isNaN(positive) ? 0 : positive) - (Number.isNaN(negative) ? 0 : negative);
  }

  const markScored = Number(item?.markScored);
  return Number.isNaN(markScored) ? 0 : markScored;
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);
  const [, setInitial] = useState(true);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    setInitial(false);
    const listener = () => setMatches(media.matches);
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, [matches, query]);

  return matches;
}

const CustomCloseButton = ({ onClick }) => (
  <Button
    variant="outline"
    size="icon"
    onClick={onClick}
    className="absolute left-1/2 -translate-x-1/2 -top-20 h-10 w-10 rounded-full bg-[var(--q3-surface-default)] hover:bg-[var(--q3-surface-default)]/90 z-50 flex items-center justify-center"
    aria-label="Close"
  >
    <X size="24" className="text-[var(--q3-neutral-default)]" />
  </Button>
);

function GroupSectionModal({ isOpen, onClose, sectionName, sectionData, isDesktop }) {
  const ModalContent = () => (
    <div className="border-1 rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px] table-fixed">
          <thead>
            <tr style={{ backgroundColor: 'var(--q3-surface-dim)' }}>
              <th className="text-left p-4 font-medium border-r-1 text-[var(--q3-neutral-default)] text-sm rounded-tl-2xl">
                Subject
              </th>
              <th className="text-center p-4 font-medium  text-[var(--q3-neutral-default)] text-sm rounded-tr-2xl">
                Score
              </th>
            </tr>
          </thead>
          <tbody>
            {sectionData?.map((item, idx) => (
              <tr key={idx}>
                <td className="p-4 border-t-1  text-[var(--q3-neutral-default)] text-sm flex items-center gap-2">
                  {item.icon && <img src={item.icon} alt={item.title} className="h-5 w-5" />}
                  <span>{item.title}</span>
                </td>
                <td className="p-4 border-l-1 border-t-1 text-center text-[var(--q3-neutral-default)] text-sm font-medium">
                  <div className="flex flex-row items-center w-full justify-center">
                    <p className="text-sm md:text-xl" style={{ color: item?.color }}>
                      {formatScoreDisplay(getEffectiveMarkScored(item))}
                    </p>
                    <p className="text-[var(--q3-neutral-light)] text-[10px] md:text-xs ml-[2px]">
                      /{formatScoreDisplay(item?.totalScore)}
                    </p>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent showCloseButton={false} className="p-6 w-max min-w-[700px] backdrop-blur-sm">
          <div className="relative h-full max-h-[80vh]">
            <CustomCloseButton onClick={onClose} />
            <div className="h-full overflow-y-auto">
              <h2 className="text-xl font-semibold text-center mb-6" style={{ color: 'var(--q3-neutral-default)' }}>
                {sectionName} - Subject Breakdown
              </h2>
              <ModalContent />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent showCloseButton={false} className="max-h-[90vh] py-0 px-4 backdrop-blur-sm">
        <div className="relative h-full">
          <CustomCloseButton onClick={onClose} />
          <div className="h-full overflow-y-auto pt-8">
            <h2 className="text-xl font-semibold text-center mb-6" style={{ color: 'var(--q3-neutral-default)' }}>
              {sectionName} - Subject Breakdown
            </h2>
            <ModalContent />
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export default function OverallScoreCard({
  overallData,
  subjectWiseData,
  isPercentileAvailable,
  groupSections,
  testShortTitle,
}) {
  const { isDarkTheme } = useContext(ThemeContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const isDesktop = useMediaQuery('(min-width: 800px)');

  const handleSectionButtonClick = (sectionName, sectionData) => {
    setSelectedSection({ name: sectionName, data: sectionData });
    setModalOpen(true);
  };

  const getSectionTotalScore = (sectionData) => {
    return sectionData.reduce((sum, item) => sum + (Number(getEffectiveMarkScored(item)) || 0), 0);
  };

  const getSectionMaxScore = (sectionData) => {
    return sectionData.reduce((sum, item) => sum + (Number(item?.totalScore) || 0), 0);
  };

  return (
    <>
      <div
        className={`relative w-full flex flex-1 ${
          isPercentileAvailable ? 'flex-col h-[258px]' : 'flex-col md:flex-row'
        } rounded-xl border border-[var(--q3-stroke-light)] bg-gradient-to-t from-[var(--q3-surface-dim)] to-[var(--q3-surface-default)]`}
      >
        {/* Blue Highlight Bar */}
        <div
          className={`absolute left-0 ${
            isPercentileAvailable ? 'top-1/4 h-1/4' : 'top-1/4 h-1/4 md:h-1/2'
          } w-[6px] bg-[var(--q3-blue-normal)] rounded-r-full`}
        />

        {/* Quizrr Stamp */}
        <div className="absolute top-0 right-0">
          <img src={isDarkTheme ? QuizrrStampDark : QuizrrStampLight} alt="quizrr stamp" />
        </div>

        {/* Score Section */}
        <div className={`flex flex-col ${isPercentileAvailable ? 'ml-6 mt-5 mb-4' : 'ml-6 mt-5 mb-2 md:mb-8'}`}>
          <p className="text-[var(--q3-neutral-default)] text-[16px] font-bold">
            {testShortTitle ? `${testShortTitle} Score` : 'Overall Score'}
          </p>
          <div className="flex flex-row items-end mt-3">
            <p className="md:text-[90px] text-5xl text-[var(--q3-blue-normal)] font-black leading-none">
              {formatScoreDisplay(overallData?.markScored)}
            </p>
            <p className="text-xl md:text-3xl text-[var(--q3-neutral-light)] pb-2">
              /{formatScoreDisplay(overallData?.totalScore)}
            </p>
          </div>

          {/* Subject-wise Scores (vertical layout for percentile case)  */}
          {isPercentileAvailable && !groupSections && subjectWiseData && Object?.values(subjectWiseData).length > 0 && (
            <div className="flex flex-row flex-wrap mt-10 gap-x-6 gap-y-2">
              {Object?.values(subjectWiseData)?.map((item) => (
                <div className="flex flex-col" key={item?.title}>
                  <p className="text-[var(--q3-neutral-light)] text-[10px] md:text-xs">{item?.title} Score</p>
                  <div className="flex flex-row items-center">
                    <p className="text-sm md:text-xl" style={{ color: item?.color }}>
                      {formatScoreDisplay(getEffectiveMarkScored(item))}
                    </p>
                    <p className="text-[var(--q3-neutral-light)] text-[10px] md:text-xs ml-[2px]">
                      /{formatScoreDisplay(item?.totalScore)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Group Sections Cards */}
        {groupSections && Object.keys(groupSections).length > 0 && (
          <div className="flex flex-row ml-6 md:ml-20 mb-4 md:mb-10 mt-3 md:mt-0 items-end gap-x-9 gap-y-4 max-w-[600px] w-full">
            {Object.entries(groupSections).map(([sectionName, sectionData], index) => (
              <div className="flex flex-col items-start gap-1 md:gap-2" key={sectionName}>
                <p className="text-[var(--q3-neutral-light)] text-sm md:text-base font-medium">{sectionName} Score</p>
                <div className="flex flex-row items-center">
                  <p
                    className={`text-base md:text-2xl font-bold ${index == 0 ? 'text-[var(--q3-violet-normal)]' : 'text-[var(--q3-lime-normal)]'}`}
                  >
                    {formatScoreDisplay(getSectionTotalScore(sectionData))}
                  </p>
                  <p className="text-[var(--q3-neutral-light)] text-xs md:text-sm font-normal ml-[3px]">
                    /{formatScoreDisplay(getSectionMaxScore(sectionData))}
                  </p>
                </div>
                <Button
                  onClick={() => handleSectionButtonClick(sectionName, sectionData)}
                  variant="link"
                  asChild
                  className="has-[>svg]:px-0 p-0 h-auto gap-1"
                >
                  <div className="flex items-center gap-1 cursor-pointer text-sm text-[var(--q3-neutral-default)] hover:text-[var(--q3-accent-normal)]">
                    <span className="text-[10px] md:text-xs underline underline-offset-4 decoration-[var(--q3-neutral-default)] hover:decoration-[var(--q3-accent-normal)]">
                      View Breakdown
                    </span>
                    <ChevronRight className="w-[16px] h-[16px]" />
                  </div>
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Subject-wise Scores on the side (original layout for non-percentile case) */}
        {!isPercentileAvailable && !groupSections && subjectWiseData && Object?.values(subjectWiseData).length > 0 && (
          <div className="flex flex-row flex-wrap ml-6 md:ml-16 mt-5 mb-4 md:mb-10 items-end gap-x-6 gap-y-2 max-w-[450px]">
            {Object.values(subjectWiseData).map((item) => (
              <div className="flex flex-col" key={item?.title}>
                <div className="flex flex-row items-center">
                  <p className="text-sm md:text-xl" style={{ color: item?.color }}>
                    {formatScoreDisplay(getEffectiveMarkScored(item))}
                  </p>
                  <p className="text-[var(--q3-neutral-light)] text-[10px] md:text-xs">
                    /{formatScoreDisplay(item?.totalScore)}
                  </p>
                </div>
                <p className="text-[var(--q3-neutral-light)] text-[10px] md:text-xs">{item?.title} Score</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Group Section Modal */}
      {selectedSection && (
        <GroupSectionModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          sectionName={selectedSection.name}
          sectionData={selectedSection.data}
          isDesktop={isDesktop}
        />
      )}
    </>
  );
}
